/**
 * «Агенты» — Spec 027: named orchestrators, a section of its own (Kostya, 30.09: «просто
 * новый раздел как работа»). A row says the agent's name, what it is for and where my
 * computer stands with it: approved, or which version waits for my «да». On a computer the
 * card opens on the right.
 */

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Plus } from "lucide-react";

import { Agents } from "@/api/resources";
import type { AgentOut } from "@/api/types";
import { NewAgentSheet } from "@/components/AgentForm";
import { DevicesBlock } from "@/components/Devices";
import { Button } from "@/components/ui/Button";
import { SkeletonList } from "@/components/ui/Skeleton";
import { ago, EmptyNote, ListPane, Pill, ScreenHeader, useIsDesktop } from "@/kit";
import { approvalLine, runWhere } from "@/lib/agents";
import { cn } from "@/lib/cn";
import { pluralRu } from "@/lib/format";
import { AgentScreen } from "@/screens/AgentScreen";

/** How many agents wait for my «да» on their current version. */
export function awaitingMine(agents: AgentOut[]): number {
  return agents.filter((a) => !a.archived_at && a.my_approved_version !== a.version).length;
}

export function AgentsScreen() {
  const desktop = useIsDesktop();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const archived = params.get("archived") === "1";
  const list = useQuery({ queryKey: ["agents", "list", archived], queryFn: () => Agents.list(archived) });

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const open = params.get("open");
  const openAgent = (id: string) => (desktop ? setParam("open", id) : navigate(`/agents/${id}`));
  const rows = list.data ?? [];
  const waiting = awaitingMine(rows);

  const body = (
    <div className="flex flex-col gap-3">
      <ScreenHeader
        title="Агенты"
        sub={waiting ? `${waiting} ${pluralRu(waiting, "ждёт", "ждут", "ждут")} твоего одобрения` : "Работают в терминале по своей карточке"}
        trailing={
          <Button size="compact" variant="secondary" icon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={() => setCreating(true)}>
            Агент
          </Button>
        }
      />
      {list.isLoading && <SkeletonList count={3} />}
      {list.isError && (
        <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void list.refetch()}>Повторить</Button>}>
          Не удалось загрузить агентов.
        </EmptyNote>
      )}
      {list.data && rows.length === 0 && (
        <EmptyNote action={!archived && <Button size="compact" variant="secondary" onClick={() => setCreating(true)}>Создать агента</Button>}>
          {archived ? "В архиве пусто." : "Агентов пока нет. Агент — названная система: запускается в терминале и работает по своей карточке."}
        </EmptyNote>
      )}
      {rows.length > 0 && (
        <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
          {rows.map((a) => {
            const line = approvalLine(a);
            return (
              <li key={a.id} className="border-b border-p-border last:border-0">
                <button type="button" onClick={() => openAgent(a.id)}
                  className={cn("flex w-full items-start gap-3 px-4 py-3 text-left press-surface", open === a.id && "bg-p-surface-2")}>
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="text-[15px] font-semibold leading-5 text-p-text">{a.name}</span>
                    {a.purpose_md && <span className="line-clamp-2 text-meta text-p-text-muted">{a.purpose_md}</span>}
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <Pill tone={line.tone}>{line.text}</Pill>
                      <span className="text-meta text-p-text-muted">изменён {ago(a.updated_at)}</span>
                    </span>
                    {a.active_run && <span className="text-meta text-p-text">{runWhere(a.active_run)}</span>}
                  </span>
                  <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-p-text-muted" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <button type="button" className="min-h-11 self-start text-meta font-semibold text-p-link" onClick={() => setParam("archived", archived ? null : "1")}>
        {archived ? "Все агенты" : "Архив"}
      </button>
      {!archived && <DevicesBlock />}
      <NewAgentSheet
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(a) => {
          setCreating(false);
          openAgent(a.id);
        }}
      />
    </div>
  );

  if (!desktop) return body;
  return (
    <ListPane
      list={body}
      listWidth={400}
      open={Boolean(open)}
      onClose={() => setParam("open", null)}
      pane={open ? <AgentScreen key={open} agentId={open} embedded /> : null}
      placeholder={rows.length ? <EmptyNote>Выбери агента слева — карточка откроется здесь.</EmptyNote> : null}
    />
  );
}
